import { useEffect } from 'react'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Layout } from '@/components/Layout'
import { FamiliarList } from '@/pages/FamiliarList'
import { CreateFamiliar } from '@/pages/CreateFamiliar'
import { FamiliarHub } from '@/pages/FamiliarHub'
import { FamiliarOverview } from '@/pages/FamiliarOverview'
import { FamiliarConstellation } from '@/pages/FamiliarConstellation'
import { Chat } from '@/pages/Chat'
import { Memory } from '@/pages/Memory'
import { Channels } from '@/pages/Channels'
import { CovenantPage } from '@/pages/Covenant'
import { Circles } from '@/pages/Circles'
import { CircleDetail } from '@/pages/CircleDetail'
import { RealmDetail } from '@/pages/RealmDetail'
import { Settings } from '@/pages/Settings'
import { GateCallback } from '@/pages/GateCallback'
import { useAuth } from '@/store/auth'
import { gateLoginUrl } from '@/lib/oneLogin'
import { buildGateLoginUrl, callbackRedirectUri, makeState, rememberState } from '@/lib/gateSso'

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30_000 } } })

/** Validates the session cookie once on mount (via /auth/me) before routing. */
function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuth((s) => s.status)
  const loadMe = useAuth((s) => s.loadMe)
  useEffect(() => {
    loadMe()
  }, [loadMe])
  if (status === 'loading') return <div className="cp-empty">Loading…</div>
  return <>{children}</>
}

/** The invisible standard (2026-08-04): there is no login page here. An
 *  anonymous visitor is SILENTLY sent to the gate and returned signed in.
 *
 *  Two mechanisms, chosen by whether the backend advertises a gate client id
 *  (code-handoff on):
 *  - code-handoff (gateClientId present, Phase 0a): redirect with our client_id
 *    + registered /auth/callback + a one-time state; the gate returns a code the
 *    backend redeems for Conclave's OWN session cookie. Survives the gate's
 *    move to diwata.id.
 *  - legacy (no gateClientId): the plain-`next` bounce that reads the shared
 *    .diwata.domains cookie — only works same-registrable-domain. */
function RequireAuth() {
  const status = useAuth((s) => s.status)
  const gateOrigin = useAuth((s) => s.gateOrigin)
  const gateClientId = useAuth((s) => s.gateClientId)

  useEffect(() => {
    if (status !== 'anon' || !gateOrigin) return
    if (gateClientId) {
      // Code-handoff: park a fresh state (CSRF binding) + where we were headed,
      // then hand off to the gate with our client id and registered callback.
      const state = makeState()
      const from = window.location.pathname + window.location.search + window.location.hash
      rememberState(sessionStorage, state, from)
      const redirectUri = callbackRedirectUri(window.location.origin)
      window.location.assign(buildGateLoginUrl(gateOrigin, gateClientId, redirectUri, state))
    } else {
      // Legacy shared-cookie bounce.
      window.location.assign(gateLoginUrl(gateOrigin, window.location.href))
    }
  }, [status, gateOrigin, gateClientId])

  if (status === 'authed') return <Outlet />
  if (status === 'anon' && !gateOrigin) {
    // No gate to bounce to (backend down or GATE_PUBLIC_ORIGIN unset) — say
    // so quietly. Never a login form: the gate is the only door.
    return <div className="cp-empty">Sign-in is unavailable — the gate could not be reached.</div>
  }
  return null // loading, or mid-bounce to the gate
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AuthGate>
          <Routes>
            {/* The gate's registered landing — public (no session yet): it
                redeems the one-time code, then routes on. Must sit OUTSIDE
                RequireAuth or it would bounce straight back to the gate. */}
            <Route path="auth/callback" element={<GateCallback />} />
            <Route element={<RequireAuth />}>
              <Route element={<Layout />}>
                <Route index element={<FamiliarList />} />
                <Route path="create" element={<CreateFamiliar />} />

                <Route path="familiar/:id" element={<FamiliarHub />}>
                  <Route index element={<FamiliarOverview />} />
                  <Route path="chat" element={<Chat />} />
                  <Route path="memory" element={<Memory />} />
                  <Route path="channels" element={<Channels />} />
                  <Route path="covenant" element={<CovenantPage />} />
                  <Route path="constellation" element={<FamiliarConstellation />} />
                  <Route
                    path="adepts"
                    element={<div className="cp-empty">Adept delegation — coming soon.</div>}
                  />
                </Route>

                <Route path="circles" element={<Circles />} />
                <Route path="circles/:id" element={<CircleDetail />} />
                <Route path="realms/:realmId" element={<RealmDetail />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Route>
          </Routes>
        </AuthGate>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
